import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  EmbeddedViewRef,
  EnvironmentInjector,
  inject,
  input,
  TemplateRef,
} from '@angular/core';
import { Tooltip, TooltipVariant } from './tooltip';

export type TooltipPosition = 'above' | 'below' | 'left' | 'right';

/**
 * Distance between the trigger and the bubble border. The arrow pseudo
 * (`--tooltip-arrow-height`, 0.45rem ≈ 7px) protrudes into this gap, leaving
 * ~3px of air between the arrow tip and the trigger.
 */
const GAP = 10;
/** Minimum clearance kept between the bubble and the viewport edges. */
const VIEWPORT_MARGIN = 8;
/**
 * Minimum distance from a bubble corner to the arrow centre: the corner
 * radius (0.5rem = 8px) plus half the arrow base (~7px), rounded up — keeps
 * the arrow clear of the rounded corners.
 */
const ARROW_EDGE_INSET = 16;
/** Touch hold before the bubble appears — matTooltip's long-press delay. */
const LONG_PRESS_DELAY = 500;
/** How long a long-press bubble lingers after the finger lifts. */
const TOUCH_HIDE_DELAY = 1500;

const POSITION_CLASSES = [
  'tooltip-pos-above',
  'tooltip-pos-below',
  'tooltip-pos-left',
  'tooltip-pos-right',
] as const;

/**
 * Hover/focus tooltip trigger — the replacement for `matTooltip`. Creates an
 * `andor-tooltip` bubble on demand, portals it to `<body>` in the browser top
 * layer (Popover API, same mechanism as `andor-context-menu`, so it renders
 * above CDK overlays like Material dialogs), and positions it on the side of
 * the host element requested via `tooltipPosition`. When the visible viewport
 * lacks room on that side, the bubble flips to the opposite side; on the
 * cross axis it is centred on the trigger and clamped to the viewport.
 *
 * `andorTooltip` accepts either a plain string or a `TemplateRef`: a template
 * is instantiated and handed to the bubble as projected content (the same
 * `<ng-content>` slot inline usage fills), so anchored tooltips can carry
 * complex markup too. Template content wins over `tooltipTitle`-less centring
 * rules exactly like inline projection does.
 *
 * While visible, the trigger is watched with an `IntersectionObserver`: the
 * tooltip hides when the trigger leaves the DOM (including detach-without-
 * destroy, e.g. a recycled cdkVirtualFor row), becomes `display: none`, or is
 * fully scrolled/clipped out of view — `mouseleave` cannot fire in any of
 * those cases. Consequently a focusin on a fully-clipped trigger hides the
 * bubble right away, which is intended.
 *
 * Triggers are split by input type, because touch has no hover to end a
 * show: a tap fires `pointerenter` and compatibility mouse events and focuses
 * the button, and none of the matching leave/blur events arrive until the
 * next tap elsewhere — the bubble would stick. So hover (`pointerenter`/
 * `pointerleave`) ignores touch, focus shows only for keyboard focus
 * (`:focus-visible`), and touch gets matTooltip's long press instead: hold to
 * show, and the bubble hides shortly after the finger lifts.
 */
@Directive({
  selector: '[andorTooltip]',
  standalone: true,
  exportAs: 'andorTooltip',
  host: {
    '(pointerenter)': 'onPointerEnter($event)',
    '(pointerleave)': 'onPointerLeave($event)',
    '(pointerdown)': 'onPointerDown($event)',
    '(pointerup)': 'onPointerUp($event)',
    '(pointercancel)': 'onPointerUp($event)',
    '(focusin)': 'onFocusIn($event)',
    '(focusout)': 'hide()',
  },
})
export class TooltipDirective {
  andorTooltip = input.required<string | TemplateRef<unknown>>();
  tooltipTitle = input<string | null>(null);
  tooltipVariant = input<TooltipVariant>('default');
  tooltipPosition = input<TooltipPosition>('above');
  tooltipDisabled = input(false);

  private readonly _elementRef = inject(ElementRef);
  private readonly _appRef = inject(ApplicationRef);
  private readonly _envInjector = inject(EnvironmentInjector);

  private _ref: ComponentRef<Tooltip> | null = null;
  // Set when the bubble was created with template content projected into it.
  private _projectedTemplate: TemplateRef<unknown> | null = null;
  private _embeddedView: EmbeddedViewRef<unknown> | null = null;
  private _visible = false;
  private _rafId: number | null = null;
  // Watches the host while the tooltip is visible; see _addListeners().
  private _intersectionObserver: IntersectionObserver | null = null;
  private _longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private _touchHideTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Keep an already-created bubble in sync with the directive inputs (e.g.
    // bound text that changes while the tooltip is showing). `setInput` marks
    // the bubble's view dirty; the reposition waits a frame so it measures the
    // re-rendered content.
    effect(() => {
      const text = this.andorTooltip();
      const title = this.tooltipTitle();
      const variant = this.tooltipVariant();
      const disabled = this.tooltipDisabled();
      if (!this._ref) return;
      if (disabled) {
        this.hide();
        return;
      }
      this._syncInputs(text, title, variant);
      if (this._visible) {
        requestAnimationFrame(() => {
          if (this._visible) this._position();
        });
      }
    });

    inject(DestroyRef).onDestroy(() => {
      this._clearTouchTimers();
      this._removeListeners();
      this._destroyBubble();
    });
  }

  protected onPointerEnter(event: PointerEvent): void {
    if (event.pointerType !== 'touch') this.show();
  }

  protected onPointerLeave(event: PointerEvent): void {
    if (event.pointerType !== 'touch') this.hide();
  }

  protected onPointerDown(event: PointerEvent): void {
    if (event.pointerType !== 'touch') return;
    this._clearTouchTimers();
    this._longPressTimer = setTimeout(() => {
      this._longPressTimer = null;
      this.show();
    }, LONG_PRESS_DELAY);
  }

  /**
   * Finger lifted, or the browser took the gesture over (scroll, pinch) and
   * sent `pointercancel`: a short tap never shows the bubble, a long press
   * lets it linger briefly so it can be read.
   */
  protected onPointerUp(event: PointerEvent): void {
    if (event.pointerType !== 'touch') return;
    this._clearTouchTimers();
    if (this._visible) {
      this._touchHideTimer = setTimeout(() => {
        this._touchHideTimer = null;
        this.hide();
      }, TOUCH_HIDE_DELAY);
    }
  }

  /**
   * Keyboard focus only. A tap or click focuses the trigger too, and that
   * focus outlives the pointer — on touch it would keep the bubble up until
   * the next tap elsewhere.
   */
  protected onFocusIn(event: FocusEvent): void {
    if ((event.target as Element).matches(':focus-visible')) this.show();
  }

  protected show(): void {
    // A fresh show (e.g. a second long press) must not be cut short by the
    // previous press's hide timer.
    if (this._touchHideTimer !== null) {
      clearTimeout(this._touchHideTimer);
      this._touchHideTimer = null;
    }
    if (this.tooltipDisabled() || !this.andorTooltip()) return;
    const ref = this._ensureCreated();
    this._syncInputs(this.andorTooltip(), this.tooltipTitle(), this.tooltipVariant());
    const bubbleEl = ref.location.nativeElement as HTMLElement;
    // Promote into the top layer BEFORE measuring — a `popover` element is
    // `display: none` until shown, and would measure 0×0.
    this._promoteToTopLayer(bubbleEl);
    // Render the (possibly changed) content before the flip math measures it.
    // Projected template content lives in its own view, checked separately.
    this._embeddedView?.detectChanges();
    ref.changeDetectorRef.detectChanges();
    this._position();
    this._visible = true;
    this._addListeners();
    // Add the class a frame later so the opacity transition runs on first show.
    requestAnimationFrame(() => {
      if (this._visible) bubbleEl.classList.add('tooltip-visible');
    });
  }

  protected hide(): void {
    this._clearTouchTimers();
    if (!this._visible) return;
    this._visible = false;
    this._removeListeners();
    // Fade out in place; the bubble stays in the (invisible, pointer-inert)
    // top layer for instant reuse and is removed when the directive dies.
    (this._ref?.location.nativeElement as HTMLElement | undefined)?.classList.remove(
      'tooltip-visible'
    );
  }

  private _clearTouchTimers(): void {
    if (this._longPressTimer !== null) {
      clearTimeout(this._longPressTimer);
      this._longPressTimer = null;
    }
    if (this._touchHideTimer !== null) {
      clearTimeout(this._touchHideTimer);
      this._touchHideTimer = null;
    }
  }

  private _ensureCreated(): ComponentRef<Tooltip> {
    const content = this.andorTooltip();
    const template = content instanceof TemplateRef ? content : null;
    // Projected nodes can only be supplied at creation time, so a changed
    // template (or a switch between string and template) rebuilds the bubble.
    if (this._ref && template !== this._projectedTemplate) {
      this._destroyBubble();
    }
    if (this._ref) return this._ref;

    let projectableNodes: Node[][] | undefined;
    if (template) {
      this._embeddedView = template.createEmbeddedView(undefined);
      this._appRef.attachView(this._embeddedView);
      projectableNodes = [this._embeddedView.rootNodes];
    }
    this._projectedTemplate = template;
    const ref = createComponent(Tooltip, {
      environmentInjector: this._envInjector,
      projectableNodes,
    });
    (ref.location.nativeElement as HTMLElement).classList.add('tooltip-anchored');
    this._appRef.attachView(ref.hostView);
    document.body.appendChild(ref.location.nativeElement);
    this._ref = ref;
    return ref;
  }

  private _destroyBubble(): void {
    if (this._ref) {
      const bubbleEl = this._ref.location.nativeElement as HTMLElement;
      this._appRef.detachView(this._ref.hostView);
      this._ref.destroy();
      bubbleEl.remove();
      this._ref = null;
    }
    if (this._embeddedView) {
      this._appRef.detachView(this._embeddedView);
      this._embeddedView.destroy();
      this._embeddedView = null;
    }
    this._projectedTemplate = null;
    this._visible = false;
  }

  private _syncInputs(
    content: string | TemplateRef<unknown>,
    title: string | null,
    variant: TooltipVariant
  ): void {
    // With template content the bubble ignores `text` anyway (projection wins);
    // pass null so nothing stale lingers if the input later becomes a string.
    this._ref?.setInput('text', typeof content === 'string' ? content : null);
    this._ref?.setInput('title', title);
    this._ref?.setInput('variant', variant);
  }

  /**
   * Same top-layer promotion as `andor-context-menu`: `manual` mode disables
   * light-dismiss (this directive owns show/hide). Feature-detected — without
   * Popover support the bubble falls back to `z-index: var(--z-index-popover)`.
   */
  private _promoteToTopLayer(bubbleEl: HTMLElement & { showPopover?: () => void }): void {
    if (typeof bubbleEl.showPopover !== 'function') return;
    if (bubbleEl.getAttribute('popover') !== 'manual') {
      bubbleEl.setAttribute('popover', 'manual');
    }
    try {
      if (!bubbleEl.matches(':popover-open')) bubbleEl.showPopover();
    } catch {
      // Not connected, or `:popover-open` unsupported — ignore and fall back.
    }
  }

  private _position(): void {
    const bubbleEl = this._ref?.location.nativeElement as HTMLElement | undefined;
    if (!bubbleEl) return;
    const triggerRect = (this._elementRef.nativeElement as HTMLElement).getBoundingClientRect();
    // Degenerate rect means the trigger is detached or `display: none` —
    // recomputing would place the bubble at (0, 0). Keep the last position;
    // the state is transient anyway, since the IntersectionObserver hides the
    // bubble within a frame.
    if (triggerRect.width === 0 && triggerRect.height === 0) return;

    const width = bubbleEl.offsetWidth;
    const height = bubbleEl.offsetHeight;
    const spaceAbove = triggerRect.top;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceLeft = triggerRect.left;
    const spaceRight = window.innerWidth - triggerRect.right;
    const neededVertical = height + GAP + VIEWPORT_MARGIN;
    const neededHorizontal = width + GAP + VIEWPORT_MARGIN;

    // Viewport flip, same policy as andor-context-menu: move to the opposite
    // side only when the preferred side lacks room AND the other side has more.
    let position = this.tooltipPosition();
    if (position === 'above' && spaceAbove < neededVertical && spaceBelow > spaceAbove) {
      position = 'below';
    } else if (position === 'below' && spaceBelow < neededVertical && spaceAbove > spaceBelow) {
      position = 'above';
    } else if (position === 'left' && spaceLeft < neededHorizontal && spaceRight > spaceLeft) {
      position = 'right';
    } else if (position === 'right' && spaceRight < neededHorizontal && spaceLeft > spaceRight) {
      position = 'left';
    }

    let top = 0;
    let left = 0;
    switch (position) {
      case 'above':
        top = triggerRect.top - GAP - height;
        left = triggerRect.left + triggerRect.width / 2 - width / 2;
        break;
      case 'below':
        top = triggerRect.bottom + GAP;
        left = triggerRect.left + triggerRect.width / 2 - width / 2;
        break;
      case 'left':
        left = triggerRect.left - GAP - width;
        top = triggerRect.top + triggerRect.height / 2 - height / 2;
        break;
      case 'right':
        left = triggerRect.right + GAP;
        top = triggerRect.top + triggerRect.height / 2 - height / 2;
        break;
    }
    // Clamp into the viewport — primarily for the centred cross axis; on the
    // main axis it is the last resort when neither side truly fits.
    left = Math.min(Math.max(left, VIEWPORT_MARGIN), window.innerWidth - width - VIEWPORT_MARGIN);
    top = Math.min(Math.max(top, VIEWPORT_MARGIN), window.innerHeight - height - VIEWPORT_MARGIN);

    bubbleEl.style.top = `${top}px`;
    bubbleEl.style.left = `${left}px`;

    // Arrow: tell the stylesheet which edge faces the trigger (reposition can
    // change the flip outcome, so this runs on every pass) and where along
    // that edge the trigger centre sits — computed from the CLAMPED position,
    // so the arrow keeps pointing at the trigger when the bubble is pinned to
    // a viewport edge. Clamped clear of the rounded corners; the bounds
    // collapse to the bubble centre for bubbles narrower than two insets.
    bubbleEl.classList.remove(...POSITION_CLASSES);
    bubbleEl.classList.add(`tooltip-pos-${position}`);
    const isVertical = position === 'above' || position === 'below';
    const target = isVertical
      ? triggerRect.left + triggerRect.width / 2 - left
      : triggerRect.top + triggerRect.height / 2 - top;
    const size = isVertical ? width : height;
    const lo = Math.min(ARROW_EDGE_INSET, size / 2);
    const hi = Math.max(size - ARROW_EDGE_INSET, size / 2);
    const offset = Math.min(Math.max(target, lo), hi);
    bubbleEl.style.setProperty('--tooltip-arrow-offset', `${offset}px`);
  }

  private _onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') this.hide();
  };

  private _onScrollOrResize = (): void => {
    if (this._rafId !== null) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      if (this._visible) this._position();
    });
  };

  private _addListeners(): void {
    document.addEventListener('keydown', this._onKeydown);
    window.addEventListener('scroll', this._onScrollOrResize, true);
    window.addEventListener('resize', this._onScrollOrResize);
    // Hide when the trigger fully leaves the viewport or the DOM. `mouseleave`
    // never fires when the host is detached without the directive being
    // destroyed — e.g. cdkVirtualFor's recycle strategy caches removed row
    // views instead of destroying them — so the bubble would otherwise stay
    // visible forever. The observer also covers `display: none` and full
    // clipping by scroll ancestors. The observe-time callback reports a
    // hovered trigger as intersecting, so showing is never spuriously undone.
    // (A detach + reattach within the same frame produces no entry, but no
    // interaction path gets there.) Guarded so a double show — mouseenter
    // followed by focusin — reuses the existing observer.
    if (typeof IntersectionObserver !== 'undefined' && !this._intersectionObserver) {
      this._intersectionObserver = new IntersectionObserver((entries) => {
        // Entries are chronological — only the latest state matters.
        if (!entries[entries.length - 1].isIntersecting) this.hide();
      });
      this._intersectionObserver.observe(this._elementRef.nativeElement as HTMLElement);
    }
  }

  private _removeListeners(): void {
    document.removeEventListener('keydown', this._onKeydown);
    window.removeEventListener('scroll', this._onScrollOrResize, true);
    window.removeEventListener('resize', this._onScrollOrResize);
    this._intersectionObserver?.disconnect();
    this._intersectionObserver = null;
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }
}
